import { useEffect, useRef } from 'react';
import { ArrowRight, Building2, Clock, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { StatCard } from '@/components/stat-card';
import { VITE_MAPBOX_TOKEN } from '@/utils/envs';
import { formatNumber } from '@/utils/format';

import type { DispatchPlace, DispatchPlaceSummary } from '@/features/dispatch-places/dispatch-places.types';
import type { MapboxInstance, MapboxMap, MapboxMarker } from '@/utils/mapbox-cdn.types';

const MAPBOX_VERSION = '3.12.0';
const MAPBOX_CDN_BASE = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}`;

// ── Props ─────────────────────────────────────────────────────────────────────

interface DispatchPlacesSectionProps {
  summary: DispatchPlaceSummary | undefined;
  places: DispatchPlace[];
  is_loading?: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_COLOR: Record<string, string> = {
  active: '#85c440',
  inactive: '#f87171',
  pending: '#fbbf24',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  pending: 'Pendiente',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function loadMapboxFromCDN(): Promise<MapboxInstance> {
  return new Promise((resolve) => {
    if (window.mapboxgl) {
      resolve(window.mapboxgl);
      return;
    }

    const existingLink = document.getElementById('mapbox-css');
    if (!existingLink) {
      const link = document.createElement('link');
      link.id = 'mapbox-css';
      link.rel = 'stylesheet';
      link.href = `${MAPBOX_CDN_BASE}/mapbox-gl.css`;
      document.head.appendChild(link);
    }

    const script = document.createElement('script');
    script.src = `${MAPBOX_CDN_BASE}/mapbox-gl.js`;
    script.onload = () => resolve(window.mapboxgl as MapboxInstance);
    document.head.appendChild(script);
  });
}

function buildMarkerElement(status: string): HTMLDivElement {
  const el = document.createElement('div');
  el.style.width = '14px';
  el.style.height = '14px';
  el.style.borderRadius = '50%';
  el.style.backgroundColor = STATUS_COLOR[status] ?? '#85c440';
  el.style.border = '2px solid white';
  el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.3)';
  el.style.cursor = 'pointer';
  return el;
}

function buildPopupHtml(place: DispatchPlace): string {
  const city = place.address?.city ?? '';
  const country = place.country_code.toUpperCase();
  // Explicit colors so the popup is readable on both light and dark map themes.
  return `
    <div style="font-family:sans-serif;font-size:13px;line-height:1.6;background:#1f263b;color:#f7f9fa;padding:10px 12px;border-radius:6px">
      <strong style="color:#f7f9fa">${place.name}</strong><br/>
      <span style="color:#9ca3af">${city ? `${city} &middot; ` : ''}${country}</span><br/>
      <span style="color:${STATUS_COLOR[place.status]}">&#9679; ${STATUS_LABEL[place.status]}</span>
    </div>
  `;
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Dashboard section showing dispatch place KPI cards and a Mapbox map.
 *
 * Marker strategy:
 * - Map initialisation (CDN load + style load) is async. We keep a
 *   `map_ready` ref that flips to true once the style finishes loading.
 * - A separate effect watches `props.places`. When places arrive AND the
 *   map is ready, it clears the previous markers and redraws them all.
 *   If the map isn't ready yet it registers a one-time `load` listener
 *   that will draw the markers as soon as the style finishes.
 */
export function DispatchPlacesSection(props: DispatchPlacesSectionProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const markersRef = useRef<MapboxMarker[]>([]);
  const map_ready = useRef(false);

  // ── Initialise map (runs once) ──────────────────────────────────────────
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    let cancelled = false;

    loadMapboxFromCDN().then((mapboxgl) => {
      if (cancelled || !mapContainerRef.current) return;

      mapboxgl.accessToken = VITE_MAPBOX_TOKEN;

      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [-102, 23],
        zoom: 4.5,
      });

      map.addControl(new mapboxgl.NavigationControl(), 'top-right');
      mapRef.current = map;

      map.once('load', () => {
        map_ready.current = true;
      });
    });

    return () => {
      cancelled = true;
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      mapRef.current?.remove();
      mapRef.current = null;
      map_ready.current = false;
    };
  }, []);

  // ── Draw markers whenever places data changes ───────────────────────────
  useEffect(() => {
    if (props.places.length === 0) return;

    const drawMarkers = () => {
      const map = mapRef.current;
      const mapboxgl = window.mapboxgl;
      if (!map || !mapboxgl) return;

      // Remove previous markers before redrawing
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];

      props.places.forEach((place) => {
        if (!place.location) return;

        const [longitude, latitude] = place.location.coordinates;

        const marker = new mapboxgl.Marker({ element: buildMarkerElement(place.status) })
          .setLngLat([longitude, latitude])
          .setPopup(new mapboxgl.Popup({ offset: 10 }).setHTML(buildPopupHtml(place)))
          .addTo(map);

        markersRef.current.push(marker);
      });
    };

    if (map_ready.current) {
      drawMarkers();
    } else {
      // Map style not ready yet — wait for it then draw
      const map = mapRef.current;
      if (map) {
        map.once('load', drawMarkers);
      } else {
        // Map itself not initialised yet — poll briefly until it is
        const interval = setInterval(() => {
          if (mapRef.current && map_ready.current) {
            clearInterval(interval);
            drawMarkers();
          }
        }, 100);

        return () => clearInterval(interval);
      }
    }
  }, [props.places]);

  // ── Render ──────────────────────────────────────────────────────────────
  const renderCardValue = (value: number | undefined) => {
    if (props.is_loading || value === undefined) {
      return <div className="h-8 w-16 animate-pulse rounded bg-base-300" />;
    }
    return formatNumber(value);
  };

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Puntos 99</h2>
        <Link to="/dispatch-places" className="btn btn-ghost btn-sm gap-1 text-primary">
          Ver todos
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          label="Total"
          value={renderCardValue(props.summary?.total)}
          Icon={Building2}
          tone="primary"
        />
        <StatCard
          label="Activos"
          value={renderCardValue(props.summary?.active)}
          Icon={Building2}
          tone="success"
        />
        <StatCard
          label="Inactivos"
          value={renderCardValue(props.summary?.inactive)}
          Icon={XCircle}
          tone="error"
        />
        <StatCard
          label="Pendientes"
          value={renderCardValue(props.summary?.pending)}
          Icon={Clock}
          tone="warning"
        />
      </div>

      <div className="card border border-base-300 bg-base-100 overflow-hidden">
        <div className="flex items-center gap-4 px-4 py-3 border-b border-base-300">
          {Object.entries(STATUS_COLOR).map(([status, color]) => (
            <span key={status} className="flex items-center gap-1.5 text-xs">
              <span
                className="inline-block h-3 w-3 rounded-full border border-white shadow-sm"
                style={{ backgroundColor: color }}
              />
              {STATUS_LABEL[status]}
            </span>
          ))}
        </div>
        <div ref={mapContainerRef} className={twMerge('w-full h-72')} />
      </div>
    </section>
  );
}
